import React, { forwardRef, useState, useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";

import { makeStyles, withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TablePagination from "@material-ui/core/TablePagination";
import LinearProgress from "@material-ui/core/LinearProgress";
import MuiExpansionPanel from "@material-ui/core/ExpansionPanel";
import MuiExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import MuiExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";

import EnhancedTableToolbar from "../EnhancedTableToolbar";
import EnhancedTableHead from "../EnhancedTableHead";

import { stableSort, getSorting } from "../../../utils/table";

const ExpansionPanel = withStyles({
  root: {
    boxShadow: "none",
  },
  expanded: false,
})(MuiExpansionPanel);

const ExpansionPanelSummary = withStyles({
  root: {
    marginBottom: -1,
    maxHeight: 0,
    minHeight: 0,
    "&$expanded": {
      maxHeight: 0,
      minHeight: 0,
    },
  },
  content: {
    "&$expanded": {
      margin: "12px 0",
    },
  },
  expanded: {},
})(MuiExpansionPanelSummary);

const ExpansionPanelDetails = withStyles(theme => ({
  root: {
    paddingBottom: theme.spacing(0),
  },
}))(MuiExpansionPanelDetails);

const useStyles = makeStyles(theme => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  tableWrapper: {
    overflowY: "auto",
  },
}));

export function handleSelectAllClick(event, rows, setSelected) {
  if (event.target.checked) {
    const newSelecteds = rows.map(row => row.selectIndex);
    setSelected(newSelecteds);
    return;
  }
  setSelected([]);
}

export function handleRequestSort(event, property, orderBy, order, setOrder, setOrderBy, setReturnOrderBy) {
  const isDesc = orderBy === property && order === "desc";
  setOrder(isDesc ? "asc" : "desc");
  setOrderBy(property);
  setReturnOrderBy(property, isDesc ? "asc" : "desc");
}

export function handleSelected(event, selectIndex, selected, setSelected) {
  const selectedIndex = selected.indexOf(selectIndex);
  let newSelected = [];

  if (selectedIndex === -1) {
    newSelected = newSelected.concat(selected, selectIndex);
  } else if (selectedIndex === 0) {
    newSelected = newSelected.concat(selected.slice(1));
  } else if (selectedIndex === selected.length - 1) {
    newSelected = newSelected.concat(selected.slice(0, -1));
  } else if (selectedIndex > 0) {
    newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
  }
  setSelected(newSelected);
}

export function handleChangePage(event, newPage, setPage, setReturnPage) {
  setPage(newPage);
  setReturnPage(newPage);
}

export function handleChangeRowsPerPage(event, setRowsPerPage, setReturnRowsPerPage) {
  setRowsPerPage(+event.target.value);
  setReturnRowsPerPage(+event.target.value);
}

const EnhancedTable = forwardRef((props, ref) => {
  const {
    title,
    rows,
    headRows,
    tableRowComponent: TableRowComponent,
    inProgress,
    deleteHandler,
    filterChildren,
    setReturnOrderBy,
    setReturnPage,
    setReturnRowsPerPage,
    sizeOfRows,
    resetPage,
    pageState,
    rowsPerPageState,
    deleteButton,
    selectAll,
    openFilter,
    callBackExpand,
    stateSortBy,
    allowOrder,
    oderState,
    rowProps,
    settingsCallback,
  } = props;
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState(oderState);
  const [orderBy, setOrderBy] = useState(stateSortBy);
  const [page, setPage] = useState(pageState || 0);

  useEffect(() => {
    if (pageState !== page && pageState !== undefined) setPage(pageState);
  }, [pageState, page, setPage]);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageState);
  const [expanded, setExpanded] = useState(openFilter || false);
  const dense = false;

  const isSelected = selectIndex => selected.indexOf(selectIndex) !== -1;
  const h = document.documentElement.clientHeight - 64 - 48 - 8;
  const height = document.documentElement.clientHeight - 64 - 48 - 64 - 63 - 1 - (inProgress ? 4 : 0);
  const modRows = rows.map((row, idx) => ({
    ...row,
    selectIndex: idx,
  }));

  const handleChange = expand => {
    callBackExpand(expand);
    setExpanded(expand);
  };

  useImperativeHandle(ref, () => ({
    resetPage() {
      setPage(0);
    },
  }));

  if (resetPage && page !== 0) {
    setPage(0);
  }
  const classes = useStyles();
  return (
    <Paper className={classes.paper} style={{ height: `${h}px` }}>
      <EnhancedTableToolbar
        filterList={filterChildren !== undefined}
        title={title}
        selected={selected}
        rows={modRows}
        deleteHandler={rowsToDelete => {
          deleteHandler(rowsToDelete);
          setSelected([]);
        }}
        expandHandler={() => handleChange(openFilter !== undefined ? !openFilter : !expanded)}
        deleteButton={deleteButton}
        settingsCallback={settingsCallback}
      />
      <ExpansionPanel square expanded={openFilter !== undefined ? openFilter : expanded && !inProgress}>
        <ExpansionPanelSummary aria-controls="panel1d-content" id="panel1d-header" />
        {filterChildren && <ExpansionPanelDetails>{filterChildren}</ExpansionPanelDetails>}
      </ExpansionPanel>
      <div className={classes.tableWrapper} style={{ height: `${height}px` }}>
        <Table aria-labelledby="tableTitle" size={dense ? "medium" : "small"}>
          <EnhancedTableHead
            selectAll={selectAll}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={event => handleSelectAllClick(event, modRows, setSelected)}
            onRequestSort={(event, property) =>
              handleRequestSort(event, property, orderBy, order, setOrder, setOrderBy, setReturnOrderBy)
            }
            rowCount={modRows.length}
            headRows={headRows}
            allowOrder={allowOrder}
          />

          {!inProgress && (
            <TableBody>
              {stableSort(modRows, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.selectIndex);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRowComponent
                      {...rowProps}
                      key={row.name + labelId}
                      row={row}
                      isItemSelected={isItemSelected}
                      labelId={labelId}
                      handleClick={(event, selectIndex) => handleSelected(event, selectIndex, selected, setSelected)}
                    />
                  );
                })}
            </TableBody>
          )}
        </Table>
      </div>
      {inProgress && <LinearProgress />}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={sizeOfRows || modRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        backIconButtonProps={{
          "aria-label": "Previous Page",
        }}
        nextIconButtonProps={{
          "aria-label": "Next Page",
        }}
        onChangePage={(event, newPage) => handleChangePage(event, newPage, setPage, setReturnPage)}
        onChangeRowsPerPage={event => handleChangeRowsPerPage(event, setRowsPerPage, setReturnRowsPerPage)}
      />
    </Paper>
  );
});

EnhancedTable.defaultProps = {
  inProgress: false,
  setReturnOrderBy: () => null,
  setReturnRowsPerPage: () => null,
  setReturnPage: () => null,
  sizeOfRows: null,
  resetPage: false,
  pageState: undefined,
  rowsPerPageState: 10,
  deleteHandler: () => null,
  deleteButton: true,
  selectAll: true,
  filterChildren: undefined,
  openFilter: undefined,
  callBackExpand: () => null,
  stateSortBy: "userName", // old config passcode
  allowOrder: undefined,
  oderState: "asc",
  rowProps: {},
  settingsCallback: undefined,
};

EnhancedTable.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  headRows: PropTypes.array.isRequired,
  tableRowComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  deleteHandler: PropTypes.func,
  inProgress: PropTypes.bool,
  filterChildren: PropTypes.node,
  setReturnOrderBy: PropTypes.func,
  sizeOfRows: PropTypes.number,
  setReturnPage: PropTypes.func,
  setReturnRowsPerPage: PropTypes.func,
  resetPage: PropTypes.bool,
  pageState: PropTypes.number,
  rowsPerPageState: PropTypes.number,
  deleteButton: PropTypes.bool,
  selectAll: PropTypes.bool,
  openFilter: PropTypes.bool,
  callBackExpand: PropTypes.func,
  stateSortBy: PropTypes.string,
  allowOrder: PropTypes.bool,
  oderState: PropTypes.string,
  rowProps: PropTypes.object,
  settingsCallback: PropTypes.func,
};

export default EnhancedTable;
